import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { fechaHora } from "../lib/formato";
import { useIdioma } from "../lib/idioma";

/**
 * El tablón del equipo, en la pantalla de inicio.
 *
 * Es lo contrario que las noticias de Comunidad: aquello va al portal del
 * socio, esto no sale del local. «El género llega el jueves», «Ana cambia el
 * turno del sábado», «la alarma se pone con el 4321».
 *
 * Vive en Inicio a propósito, que es la pantalla que todo el mundo abre al
 * empezar. Un tablón en una sección aparte es un tablón que nadie mira.
 */

interface Nota {
  id: string;
  texto: string;
  fijada: boolean;
  createdAt: string;
  usuario: { nombre: string } | null;
}

export default function Tablon() {
  const { t } = useIdioma();
  const clienteConsultas = useQueryClient();
  const [texto, setTexto] = useState("");
  const [fijada, setFijada] = useState(false);
  const [escribiendo, setEscribiendo] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const notas = useQuery({ queryKey: ["tablon"], queryFn: () => api<Nota[]>("/comunidad/tablon") });
  const refrescar = () => void clienteConsultas.invalidateQueries({ queryKey: ["tablon"] });

  async function poner() {
    if (!texto.trim()) return;
    setEnviando(true);
    try {
      await api("/comunidad/tablon", { metodo: "POST", cuerpo: { texto, fijada } });
      setTexto("");
      setFijada(false);
      setEscribiendo(false);
      refrescar();
    } finally {
      setEnviando(false);
    }
  }

  const lista = notas.data ?? [];
  // Sin recados y sin ganas de escribir, no se ocupa sitio en la pantalla.
  if (lista.length === 0 && !escribiendo) {
    return (
      <div className="tablon-vacio">
        <button className="boton fantasma" onClick={() => setEscribiendo(true)}>
          ✎ {t("tb.dejarRecadoEquipo")}
        </button>
      </div>
    );
  }

  return (
    <section className="tablon">
      <div className="fila separada" style={{ marginBottom: 8 }}>
        <h2>{t("tb.titulo")}</h2>
        {!escribiendo && (
          <button className="boton fantasma" onClick={() => setEscribiendo(true)}>
            ✎ {t("tb.dejarRecado")}
          </button>
        )}
      </div>

      {escribiendo && (
        <div className="tablon-escribir">
          <textarea
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder={t("tb.ejemplo")}
            maxLength={500}
            rows={2}
            autoFocus
          />
          <div className="fila separada">
            <label className="casilla" style={{ margin: 0 }}>
              <input type="checkbox" checked={fijada} onChange={(e) => setFijada(e.target.checked)} />
              <span>{t("tb.fijarArriba")}</span>
            </label>
            <div className="fila">
              <button className="boton secundario" onClick={() => setEscribiendo(false)}>
                {t("g.cancelar")}
              </button>
              <button className="boton" disabled={enviando || !texto.trim()} onClick={poner}>
                {enviando ? t("tb.poniendo") : t("tb.poner")}
              </button>
            </div>
          </div>
        </div>
      )}

      <ul className="lista-notas">
        {lista.map((nota) => (
          <li key={nota.id} className={nota.fijada ? "fijada" : undefined}>
            <div>
              <p>{nota.texto}</p>
              <span className="suave pequeno">
                {nota.usuario?.nombre ?? t("tb.alguien")} · {fechaHora(nota.createdAt)}
              </span>
            </div>
            <button
              className="boton fantasma"
              title={t("tb.quitar")}
              onClick={async () => {
                await api(`/comunidad/tablon/${nota.id}`, { metodo: "DELETE" });
                refrescar();
              }}
            >
              ✕
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
